/**
 * UpdatePrompt Component
 * Shows a prompt when a service worker update is available
 * Allows users to apply updates or dismiss them
 */

import { useServiceWorkerUpdate } from '../../hooks/usePWA';
import Button from '../ui/Button';

interface UpdatePromptProps {
  position?: 'top' | 'bottom';
  autoShow?: boolean;
  onUpdate?: () => void;
  onDismiss?: () => void;
}

export default function UpdatePrompt({
  position = 'bottom',
  autoShow = true,
  onUpdate,
  onDismiss,
}: UpdatePromptProps) {
  const { updateAvailable, isUpdating, applyUpdate, dismissUpdate } = useServiceWorkerUpdate();

  if (!updateAvailable || !autoShow) {
    return null;
  }

  const handleUpdate = async () => {
    await applyUpdate();
    onUpdate?.();
  };

  const handleDismiss = () => {
    dismissUpdate();
    onDismiss?.();
  };

  return (
    <div className={`fixed left-4 right-4 z-50 ${position === 'top' ? 'top-4' : 'bottom-4'} md:left-auto md:right-4 md:max-w-md`}>
      <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-blue-800">
              Update Available
            </h4>
            <p className="text-xs text-blue-700 mt-1">
              A new version of the app is available with improvements and bug fixes.
            </p>
            
            <div className="flex space-x-2 mt-3">
              <Button
                size="sm"
                onClick={handleUpdate}
                isLoading={isUpdating}
                className="text-xs px-3 py-1"
              >
                {isUpdating ? 'Updating...' : 'Update Now'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="text-xs px-3 py-1"
              >
                Later
              </Button>
            </div>
          </div>
          
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}